import { useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { useLocation } from "react-router-dom";

// Botón discreto en esquina para entrar/salir de pantalla completa.
//
// Por qué no auto-fullscreen sin botón:
//   El browser exige user gesture para requestFullscreen — no se puede
//   activar sin que el cliente toque algo. Si el cliente sale con ESC
//   sin querer (o el browser lo saca por algún reason), necesita una
//   forma fácil de volver.
//
// Diseño:
//   Fixed bottom-RIGHT. Opacity baja (40%) en idle para no distraer.
//   Hover sube a 80%. Tamaño chico (28px). Solo en kiosk mode (no en
//   /admin ni /setup).
//   El candado de bloqueo subió a top-right (menos accesible para
//   criaturas), liberando el bottom-right para este botón. Bottom es
//   la posición natural del fullscreen toggle en kiosks/players, así
//   que aprovechamos ese espacio.
//
// Persistencia:
//   Localstorage flag — cuando el cliente entra a fullscreen, lo
//   guardamos. Cuando sale (botón o ESC), lo limpiamos. Al cargar la
//   página, si el flag está activo, el primer click en cualquier
//   parte del documento intenta auto-fullscreen (user gesture).
const STORAGE_KEY = "juegossc_kiosk_fullscreen_wanted";

export function FullscreenButton() {
  const { pathname } = useLocation();
  const [isFullscreen, setIsFullscreen] = useState(
    () => document.fullscreenElement !== null,
  );

  // Solo registramos listeners en modo kiosko — admin/setup no
  // necesitan fullscreen. El componente se monta una sola vez en
  // App.tsx (fuera del Routes), entonces sin este guard al tope los
  // listeners se arman aunque la URL inicial sea /admin.
  const isKiosko =
    !pathname.startsWith("/admin") && !pathname.startsWith("/setup");

  // Sync con fullscreenchange del browser. Cubre 3 casos:
  //   - Cliente toca nuestro botón → cambia estado → este listener lo
  //     refleja en el ícono.
  //   - Cliente sale con ESC → listener detecta y limpia el flag.
  //   - Browser sale automáticamente (algún switching de tab raro) →
  //     listener lo nota.
  useEffect(() => {
    if (!isKiosko) return;
    function onChange() {
      const inFs = document.fullscreenElement !== null;
      setIsFullscreen(inFs);
      // Si salió, limpiar el flag para no re-intentar auto-entry sin
      // que el cliente lo pida.
      if (!inFs) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [isKiosko]);

  // Auto-reentry tras recarga: si el flag está, el primer click en el
  // documento dispara requestFullscreen (user gesture válido). Usamos
  // un listener one-shot para no molestar después.
  useEffect(() => {
    if (!isKiosko) return;
    if (localStorage.getItem(STORAGE_KEY) !== "1") return;
    if (document.fullscreenElement !== null) return;
    function tryEnter() {
      // Si requestFullscreen rechaza (permission denied, iframe, browser
      // que no soporta), limpiar el flag — sino queda forever en "1"
      // y cada recarga reintenta sin éxito, ensuciando localStorage.
      void document.documentElement
        .requestFullscreen?.()
        .catch(() => localStorage.removeItem(STORAGE_KEY));
      document.removeEventListener("click", tryEnter);
      document.removeEventListener("touchend", tryEnter);
    }
    document.addEventListener("click", tryEnter, { once: true });
    document.addEventListener("touchend", tryEnter, { once: true });
    return () => {
      document.removeEventListener("click", tryEnter);
      document.removeEventListener("touchend", tryEnter);
    };
  }, [isKiosko]);

  // Ocultar en admin/setup — el botón es solo para modo kiosko.
  if (!isKiosko) {
    return null;
  }

  async function toggle() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
        localStorage.setItem(STORAGE_KEY, "1");
      }
    } catch (e) {
      // Algunos browsers (iOS Safari, iframes) rechazan fullscreen.
      // No es crítico — solo loggeo para diagnóstico.
      console.warn("Fullscreen toggle falló:", e);
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
      title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
      // touch-action: manipulation — sin esto el browser podía meter
      // delay 300ms en móvil. fixed + z-50 para quedar siempre encima.
      // bottom-3 right-3 — el candado de bloqueo ahora vive top-right,
      // así que el bottom-right está libre y es la posición natural
      // del fullscreen toggle.
      style={{ touchAction: "manipulation" }}
      className="fixed bottom-3 right-3 z-50 rounded-lg bg-black/30 p-2 text-white/40 backdrop-blur-sm transition hover:bg-black/50 hover:text-white/90"
    >
      {isFullscreen ? (
        <Minimize2 className="h-4 w-4" />
      ) : (
        <Maximize2 className="h-4 w-4" />
      )}
    </button>
  );
}
